import React from 'react';
import { AlertCircle, RefreshCw, Search } from 'lucide-react';

export type EmptyStateVariant = 'no-data' | 'no-findings' | 'error';

interface ComplianceEmptyStateProps {
  variant: EmptyStateVariant;
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    isLoading?: boolean;
  };
}

const defaultContent: Record<EmptyStateVariant, { title: string; description: string; icon: React.ReactNode }> = {
  'no-data': {
    title: 'No compliance data',
    description: 'Click Refresh to compute compliance metrics for your organization.',
    icon: <RefreshCw className="h-12 w-12 text-ui-text-tertiary" />,
  },
  'no-findings': {
    title: 'No findings detected',
    description: 'Run a scan to detect vulnerabilities and generate compliance metrics.',
    icon: <Search className="h-12 w-12 text-ui-text-tertiary" />,
  },
  'error': {
    title: 'Failed to load data',
    description: 'Something went wrong while loading compliance data. Please try again.',
    icon: <AlertCircle className="h-12 w-12 text-red-400" />,
  },
};

export const ComplianceEmptyState: React.FC<ComplianceEmptyStateProps> = ({
  variant,
  title,
  description,
  action,
}) => {
  const content = defaultContent[variant];

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="mb-4">
        {content.icon}
      </div>
      <h3 className="text-lg font-medium text-ui-text-primary mb-2">
        {title || content.title}
      </h3>
      <p className="text-sm text-ui-text-secondary max-w-md mb-4">
        {description || content.description}
      </p>
      {action && (
        <button
          onClick={action.onClick}
          disabled={action.isLoading}
          className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {action.isLoading && <RefreshCw className="h-4 w-4 animate-spin" />}
          {action.label}
        </button>
      )}
    </div>
  );
};

export default ComplianceEmptyState;
